import React, { Component, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import Helmet from 'react-helmet';
import styled from 'styled-components';
import Header from 'components/Header';
import MainSidebar from 'components/MainSidebar';
import { Container, LoadingLayout, MAIN_HEADER_HEIGHT, MAIN_SIDEBAR_WIDTH, media } from 'ui';
import { history } from 'store';
import { getUserIdentities, logout, putMe } from 'actions/user';
import { getPlaceFiscalYears, postPlaceDefaultIdentities } from 'actions/places';
import { getTerms, getZendeskToken } from 'api/user';
import { fetchInitialData } from 'actions';
import { getCurrentUser, getSelectedPlace, selectCurrentRole } from 'selectors';
import { SERGIC_OFFER } from './constants';
import { sendImportantSnack } from 'actions/snacks';
import Communication from 'containers/Communication';
import { head, isEmpty, isEqual, orderBy } from 'lodash';
import ReactMarkdown from 'react-markdown';
import Impersonate from 'containers/Impersonate';
import Modal from 'components/Modal';
import { IdentityForm } from 'components/Profile';
import CaretakerLodge from 'containers/Dashboard/CaretakerLodge';
import AlloVoisins from 'containers/Dashboard/AlloVoisins';
import ServiceAnytime from 'containers/Dashboard/ServiceAnytime';
import get from 'lodash/get';
import { fetchExpensesSummary } from './actions/expenses';
import { Spinner } from '@tymate/margaret';
import { fetchMaintenanceCommunication } from './actions/communication';

export const Markdown = styled(ReactMarkdown)`
   font-size: ${({ theme }) => theme.size(1)};
   line-height: 1.25;

   a {
      color: ${({ theme }) => theme.primary};
      font-weight: 500;
   }
`;

const Accounts = lazy(() => import('containers/Accounts'));
const Settings = lazy(() => import('containers/Settings'));
const Notifications = lazy(() => import('containers/Notifications'));
const Documents = lazy(() => import('containers/Documents'));
const Dashboard = lazy(() => import('containers/Dashboard'));
const Decision = lazy(() => import('containers/Dashboard/Decision'));
const GeneralMeeting = lazy(() => import('containers/GeneralMeeting'));

const Wrapper = styled.div`
   background-color: ${({ theme }) => theme.background};
   color: ${({ theme }) => theme.text};
   min-height: 100vh;
   position: relative;
   z-index: 1;
`;

const Page = styled.div`
   padding-top: 56px;
   padding-bottom: 60px;

   ${media.tablet`
    padding-top: ${props => !props.isActiveMaintenance && MAIN_HEADER_HEIGHT};
    padding-left: ${MAIN_SIDEBAR_WIDTH};
    padding-bottom: 0;
  `};
`;

class App extends Component {
   state = {
      isAcceptingTerms: false,
      zendeskDirToken: null,
   };

   componentDidMount = async () => {
      const { fetchInitialData, location, user } = this.props;
      window.previousLocation = location;

      if (!user.isAuthenticated) {
         history.replace('/connexion');
         return;
      }

      this.fetchTerms();
      this.fetchZendeskToken();
      await fetchInitialData();

      if (this.props.selectedPlaceId) {
         await this.props.getUserIdentities(this.props.selectedPlaceId);
         if (Boolean(this.props.selectedPlace) && !Boolean(this.props.selectedPlace.defaultIdentity)) {
            this.handleShouldSetIdentity();
         }
         await this.props.getPlaceFiscalYears(this.props.selectedPlace);
      }
      this.fetchIsActiveMaintenance();
   };

   fetchIsActiveMaintenance = () => {
      const { getCommunication } = this.props;
      return getCommunication();
   };
   getCurrentPlaceIdentities = () => {
      const { user, selectedPlace } = this.props;

      return ((user.identitiesIdsByPlaceId || {})[selectedPlace.id] || []).map(id => user.identityById[id]);
   };

   handleSelectIdentity = async ({ selectedIdentity }) => {
      const { selectedPlaceId, postPlaceDefaultIdentities } = this.props;
      const payload = { identity: { id: selectedIdentity } };
      await postPlaceDefaultIdentities(selectedPlaceId, payload);
      this.setState({ shouldSelectIdentity: false });
   };

   handleShouldSetIdentity = () => {
      if (this.getCurrentPlaceIdentities().length === 0) {
         return;
      }

      if (this.getCurrentPlaceIdentities().length === 1) {
         this.handleSelectIdentity({
            selectedIdentity: this.getCurrentPlaceIdentities()[0].id,
         });
      }

      if (this.getCurrentPlaceIdentities().length > 1) {
         this.setState({ shouldSelectIdentity: true });
      }
   };

   UNSAFE_componentWillUpdate(nextProps) {
      const { location, currentUser } = this.props;

      if (!nextProps.user.isAuthenticated) {
         history.replace('/connexion');
         return;
      }

      if (Boolean(window.Raven) && !isEqual(currentUser, nextProps.currentUser)) {
         window.Raven.setUserContext({
            id: nextProps.currentUser.id,
            email: nextProps.currentUser.emailAddress,
         });
      }

      if (nextProps.history.action !== 'POP' && (!location.state || !location.state.modal)) {
         window.previousLocation = this.props.location;
      }
   }

   componentDidUpdate(prevProps) {
      const { location, user, fetchInitialData, selectedPlaceId, selectedFiscalYearId } = this.props;

      if (prevProps.location.pathname !== location.pathname && !location.hash) {
         if (!location.pathname.match(/(mes-comptes)\/{1}(facturiers)\/{1}(.{16})/i)) {
            window.scrollTo(0, 0);
         }
         document.documentElement.style.overflowY = 'auto';
      }

      if (Boolean(selectedPlaceId) && prevProps.selectedPlaceId !== selectedPlaceId) {
         this.props.getUserIdentities(selectedPlaceId);
         this.props.getPlaceFiscalYears(this.props.selectedPlace);
      }
      if (
         Boolean(selectedFiscalYearId) &&
         Boolean(selectedPlaceId) &&
         (prevProps.selectedFiscalYearId !== selectedFiscalYearId || prevProps.selectedPlaceId !== selectedPlaceId)
      ) {
         this.props.fetchExpensesSummary(selectedPlaceId, selectedFiscalYearId);
      }

      if (!isEqual(prevProps.place, this.props.place)) {
         this.setState({
            shouldSelectIdentity: !this.props.place.defaultIdentity && this.getCurrentPlaceIdentities().length > 1,
         });
      }

      if (!isEqual(user.impersonatedUser, prevProps.user.impersonatedUser)) {
         fetchInitialData();
      }
   }

   fetchZendeskToken = async () => {
      if (SERGIC_OFFER !== 'DIR') {
         return;
      }

      const { user } = this.props;
      const payload = { device: 'web', access_token: user.accessToken };

      const { jwt } = await getZendeskToken(payload);
      this.setState({ zendeskDirToken: jwt });
   };

   fetchTerms = async () => {
      try {
         const { entities, result } = await getTerms();
         this.setState({
            terms: head(
               orderBy(
                  result.map(id => entities.termsVersions[id]),
                  'version',
                  'desc'
               )
            ),
         });
      } catch (e) {}
   };

   handleAcceptTerms = async () => {
      const { putMe } = this.props;

      const user = {
         termVersion: this.state?.terms?.version,
      };

      this.setState({ isAcceptingTerms: true });

      await putMe({ user });

      this.setState({ isAcceptingTerms: false });
   };

   render() {
      const { currentUser, user, maintenanceData, role } = this.props;
      const { zendeskDirToken, shouldSelectIdentity } = this.state;

      if (!currentUser || isEmpty(currentUser)) {
         return null;
      }

      const hasToImpersonate = (currentUser.sergicPartner || currentUser.sergicPartnerAdmin) && !user.impersonatedUser;

      if (hasToImpersonate) {
         return <Impersonate />;
      }

      return (
         <Wrapper>
            <Modal isOpen={shouldSelectIdentity} shallNotBeDismissed>
               <IdentityForm
                  identities={this.getCurrentPlaceIdentities()}
                  onSubmit={this.handleSelectIdentity}
                  shallNotBeDismissed
               >
                  <p>
                     Vous avez plusieurs comptes sur cet immeuble. Veuillez choisir comment vous souhaitez apparaître
                     auprès des autres utilisateurs
                  </p>
               </IdentityForm>
            </Modal>

            <Helmet htmlAttributes={{ class: SERGIC_OFFER === 'DIR' ? 'dir' : '' }}>
               {SERGIC_OFFER === 'DIR' && !user.impersonatedUser && Boolean(zendeskDirToken) && (
                  <script>{`window.zESettings = { authenticate:{ jwt: '${zendeskDirToken}' } };`}</script>
               )}

               {SERGIC_OFFER === 'DIR' && Boolean(zendeskDirToken) && (
                  <script
                     id='ze-snippet'
                     src='https://static.zdassets.com/ekr/snippet.js?key=dd107297-eb16-4d6b-90d5-688b5fc8cc64'
                  />
               )}
            </Helmet>

            <Header onLogout={this.props.logout} currentUser={currentUser} />

            <MainSidebar />

            <Page isActiveMaintenance={maintenanceData?.is_active}>               
            {!role && (
                  <Container>
                     <LoadingLayout>
                        <Spinner />
                     </LoadingLayout>
                  </Container>
               )}
               {role && (
               <Suspense fallback={<div />}>
                  <Switch>
                     <Route path='/mes-echanges' component={Communication} />
                     <Route path='/mes-comptes' component={Accounts} />
                     <Route path='/mes-documents' component={Documents} />
                     <Route path='/actualites' component={Notifications} />
                     <Route path='/parametres' component={Settings} />
                     <Route path='/notifications/:notificationId' component={Dashboard} />
                     {SERGIC_OFFER === 'ESE' && <Route path='/conciergerie' component={CaretakerLodge} />}
                     <Route path='/allovoisins' component={AlloVoisins} />
                     <Route path='/serviceanytime' component={ServiceAnytime} />
                     <Route path='/decisions/:id' component={Decision} />
                     <Route path='/decisions' exact render={() => <Redirect to='/' />} />
                     <Route path='/assemblees-generales/:generalMeetingId' component={GeneralMeeting} />
                     <Route path='/' component={Dashboard} />
                  </Switch>
               </Suspense>
               )}
            </Page>
         </Wrapper>
      );
   }
}

const mapStateToProps = state => ({
   role: selectCurrentRole(state),
   user: state.user,
   currentUser: getCurrentUser(state),
   selectedPlace: getSelectedPlace(state),
   hasSkippedCheck: state.ui.hasSkippedCheck,
   selectedPlaceId: state.ui.selectedPlaceId,
   selectedFiscalYearId: get(state.ui.selectedFiscalYears, state.ui.selectedPlaceId),
   maintenanceData: state.communication.data,
});

const mapDispatchToProps = dispatch => ({
   fetchInitialData: () => dispatch(fetchInitialData()),
   putMe: (...args) => dispatch(putMe(...args)),
   logout: () => dispatch(logout()),
   sendImportantSnack: payload => dispatch(sendImportantSnack(payload)),
   getUserIdentities: placeId => dispatch(getUserIdentities(placeId)),
   getPlaceFiscalYears: place => dispatch(getPlaceFiscalYears(place?.id, place?.ioniActivated)),
   fetchExpensesSummary: (placeId, fiscalYearId) => dispatch(fetchExpensesSummary(placeId, fiscalYearId)),
   postPlaceDefaultIdentities: (...args) => dispatch(postPlaceDefaultIdentities(...args)),
   getCommunication: () => dispatch(fetchMaintenanceCommunication()),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
